import { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Chip, Avatar, Typography,
  TextField, InputAdornment, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Menu, MenuItem, Checkbox, ListItemText,
  Divider, LinearProgress, Snackbar, Alert, Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DnsIcon from "@mui/icons-material/Dns";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";

// ── Theme ─────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: "#0284c7" },
    background: { default: "#f5f7fa", paper: "#ffffff" },
    text:       { primary: "#1e293b", secondary: "#475569" },
  },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    h5:        { fontWeight: 800, letterSpacing: "-0.5px" },
    subtitle2: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgb(0 0 0/0.06),0 1px 2px -1px rgb(0 0 0/0.06)",
          border: "1px solid #e2e8f0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#f8fafc", color: "#475569", fontSize: 11, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #e2e8f0",
          padding: "10px 18px", whiteSpace: "nowrap",
        },
        body: {
          fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9",
          padding: "14px 18px", verticalAlign: "middle",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:hover td": { backgroundColor: "#f8faff" }, "&:last-child td": { borderBottom: "none" } },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 20, height: 7, backgroundColor: "#e5e7eb" },
        bar:  { borderRadius: 20 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10, backgroundColor: alpha("#0284c7", 0.04),
            "& fieldset": { borderColor: alpha("#0284c7", 0.2) },
            "&:hover fieldset": { borderColor: "#0284c7" },
            "&.Mui-focused fieldset": { borderColor: "#0284c7", borderWidth: 1.5 },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: { root: { textTransform: "none", fontWeight: 600, borderRadius: 8, fontSize: 13 } },
    },
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function efficiencyBadge(val) {
  if (val > 100) return { label: "Excellent", color: "success" };
  if (val > 70)  return { label: "Good",      color: "warning" };
  return               { label: "Poor",      color: "error"   };
}

const COL_DEFS = [
  { key: "name",       label: "Node Name"    },
  { key: "cpu",        label: "CPU Usage"    },
  { key: "memory",     label: "Memory Usage" },
  { key: "storage",    label: "Storage"      },
  { key: "cost",       label: "Total Cost"   },
  { key: "potentialSaving", label: "Potential Saving" },
  { key: "efficiency", label: "Efficiency"   },
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function EKSNodes({ nodesData = null }) {
  // Support both EKS and AKS node structures
  const RAW_DATA = Array.isArray(nodesData) 
    ? nodesData 
    : (nodesData?.nodes ?? []);

  const clusterLabel = nodesData?.clusterName ?? null;

  const [search,      setSearch]      = useState("");
  const [filtered,    setFiltered]    = useState(RAW_DATA);
  const [sortCol,     setSortCol]     = useState(null);
  const [sortDir,     setSortDir]     = useState("asc");
  const [colAnchor,   setColAnchor]   = useState(null);
  const [snack,       setSnack]       = useState({ open: false, msg: "" });
  const [visibleCols, setVisibleCols] = useState({
    name: true, cpu: true, memory: true, storage: true, cost: true, efficiency: true, potentialSaving: true,
  });

  // Reset when data changes
  useEffect(() => {
    const nodes = Array.isArray(nodesData) ? nodesData : (nodesData?.nodes ?? []);
    setFiltered(nodes);
    setSearch("");
  }, [nodesData]);

  // Search filter
  useEffect(() => {
    const nodes = Array.isArray(nodesData) ? nodesData : (nodesData?.nodes ?? []);
    const t = search.toLowerCase();
    setFiltered(
      t ? nodes.filter(n =>
        (n.privateDnsName || n.vmId || "").toLowerCase().includes(t) ||
        (n.instanceType || n.vmSize || "").toLowerCase().includes(t) ||
        (n.nodeGroup || n.nodePoolName || "").toLowerCase().includes(t)
      ) : nodes
    );
  }, [search, nodesData]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    const vals = {
      name:       [a.privateDnsName || a.vmId || "",                          b.privateDnsName || b.vmId || ""],
      cpu:        [(a.usedCPU ?? 0) / (a.totalCPU || a.vcpus || 1),           (b.usedCPU ?? 0) / (b.totalCPU || b.vcpus || 1)],
      memory:     [(a.usedMemory ?? 0) / (a.totalMemory || a.memory || 1),     (b.usedMemory ?? 0) / (b.totalMemory || b.memory || 1)],
      storage:    [a.size ?? a.totalDiskSize ?? 0,                            b.size ?? b.totalDiskSize ?? 0],
      cost:       [a.nodeCost ?? 0,                                           b.nodeCost ?? 0],
      potentialSaving: [
        (a.currentOnDemandMonthlyInstanceCost ?? 0) - (a.recommendedOnDemandMonthlyPriceValue ?? 0),
        (b.currentOnDemandMonthlyInstanceCost ?? 0) - (b.recommendedOnDemandMonthlyPriceValue ?? 0)
      ],
      efficiency: [parseFloat(a.nodeEfficiency || a.nodeEfficiency) || 0,     parseFloat(b.nodeEfficiency || b.nodeEfficiency) || 0],
    };
    const [av, bv] = vals[sortCol];
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ?  1 : -1;
    return 0;
  });

  function toggleCol(key) {
    setVisibleCols(p => ({ ...p, [key]: !p[key] }));
  }

  function exportCSV() {
    const header = "Node Name,Instance Type,CPU Usage,Memory Usage,Storage,Total Cost,Efficiency";
    const rows = filtered.map(n => [
      n.privateDnsName || n.vmId || "",
      n.instanceType || n.vmSize || "",
      ((n.usedCPU ?? 0) / (n.totalCPU || n.vcpus || 1) * 100).toFixed(1) + "%",
      ((n.usedMemory ?? 0) / (n.totalMemory || n.memory || 1) * 100).toFixed(1) + "%",
      (n.size ?? n.totalDiskSize ?? 0) + "GB",
      "$" + (n.nodeCost ?? 0).toFixed(2),
      n.nodeEfficiency || "NA",
    ].join(","));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob), download: "nodes_data.csv",
    });
    a.click();
  }

  // Summary stats
  const totalCost     = filtered.reduce((s, n) => s + (n.nodeCost ?? 0), 0);
  const avgCPU        = filtered.length
    ? filtered.reduce((s, n) => s + ((n.usedCPU ?? 0) / (n.totalCPU || n.vcpus || 1)) * 100, 0) / filtered.length : 0;
  const avgMemory     = filtered.length
    ? filtered.reduce((s, n) => s + ((n.usedMemory ?? 0) / (n.totalMemory || n.memory || 1)) * 100, 0) / filtered.length : 0;

  const statCards = [
    { label: "Total Nodes",      value: filtered.length,                icon: <DnsIcon />,          color: "#0284c7", bg: "#e0f2fe" },
    { label: "Total Cost",       value: `$${totalCost.toFixed(2)}`,     icon: <AttachMoneyIcon />,  color: "#10b981", bg: "#d1fae5" },
    { label: "Avg CPU Usage",    value: `${avgCPU.toFixed(1)}%`,        icon: <MemoryIcon />,       color: "#d97706", bg: "#fef3c7" },
    { label: "Avg Memory Usage", value: `${avgMemory.toFixed(1)}%`,     icon: <StorageIcon />,      color: "#0369a1", bg: "#bae6fd" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", p: { xs: 2, md: 3 } }}>

        {/* ── Header Card ───────────────────────────────────────────────────── */}
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: "20px 24px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha("#0284c7", 0.1), color: "#0284c7", width: 46, height: 46, borderRadius: "12px", "& svg": { fontSize: 22 } }}>
                  <DnsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, color: "text.primary" }}>
                    Nodes{clusterLabel ? ` — ${clusterLabel}` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      Node resource usage & cost analysis
                    </Typography>
                    <Chip
                      label={`${RAW_DATA.length} node${RAW_DATA.length !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: alpha("#0284c7", 0.1), color: "primary.main" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── Column Toggle Menu ────────────────────────────────────────────── */}
        <Menu 
          anchorEl={colAnchor} 
          open={Boolean(colAnchor)} 
          onClose={() => setColAnchor(null)} 
          transformOrigin={{ horizontal: "right", vertical: "top" }} 
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }} 
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 200, mt: 0.5, border: "1px solid #e2e8f0" } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Toggle Columns
            </Typography>
          </Box>
          <Divider />
          {COL_DEFS.map(col => (
            <MenuItem key={col.key} dense onClick={() => toggleCol(col.key)} sx={{ gap: 0.5 }}>
              <Checkbox size="small" checked={visibleCols[col.key]} sx={{ p: 0.5, mr: 0.5, color: "primary.main" }} />
              <ListItemText primary={col.label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          ))}
        </Menu>

        {/* ── Summary Cards ─────────────────────────────────────────────────── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(2,1fr)", lg: "repeat(4,1fr)" }, gap: 2, mb: 2.5 }}>
          {statCards.map(card => (
            <Card key={card.label}>
              <CardContent sx={{ p: "18px 20px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 11.5 }}>{card.label}</Typography>
                    <Typography variant="h5" sx={{ mt: 0.5, fontSize: 24, color: "text.primary" }}>{card.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: card.bg, color: card.color, width: 42, height: 42, borderRadius: "10px", "& svg": { fontSize: 20 } }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* ── Controls Bar ──────────────────────────────────────────────────── */}
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "flex-end", 
            mb: 2.5,
            mt: 5,
            gap: 1.5,
            flexWrap: "wrap"
          }}
        >
          <TextField
            size="small"
            placeholder="Search nodes, instance types…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
            onClick={e => setColAnchor(e.currentTarget)}
            sx={{ height: 38, px: 2 }}
          >
            Columns
          </Button>

          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={exportCSV}
            sx={{ height: 38, px: 2 }}
          >
            Export
          </Button>
        </Box>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {COL_DEFS.filter(c => visibleCols[c.key]).map(col => (
                    <TableCell key={col.key}>
                      <TableSortLabel 
                        active={sortCol === col.key} 
                        direction={sortCol === col.key ? sortDir : "asc"} 
                        onClick={() => handleSort(col.key)} 
                        sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { opacity: sortCol === col.key ? 1 : 0.3 } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No node data available.
                    </TableCell>
                  </TableRow>
                ) : sorted.map(node => {
                  const cpuPct = ((node.usedCPU ?? 0) / (node.totalCPU || node.vcpus || 1)) * 100;
                  const memPct = ((node.usedMemory ?? 0) / (node.totalMemory || node.memory || 1)) * 100;
                  const effVal = parseFloat(node.nodeEfficiency || node.nodeEfficiency) || 0;
                  const { label: effLabel, color: effColor } = efficiencyBadge(effVal);

                  return (
                    <TableRow key={node.instanceId || node.vmId}>
                      {visibleCols.name && (
                        <TableCell sx={{ minWidth: 340 }}>   {/* ← Increased minWidth for better space */}
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: alpha("#0284c7", 0.08), 
                                color: "#0284c7", 
                                width: 36, 
                                height: 36, 
                                borderRadius: "9px", 
                                "& svg": { fontSize: 17 }, 
                                flexShrink: 0, 
                                mt: 0.2 
                              }}
                            >
                              <DnsIcon />
                            </Avatar>
                            
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {/* Main Node Name */}
                              <Tooltip title={node.privateDnsName || node.vmId || "N/A"} arrow>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontSize: 13.5, 
                                    fontWeight: 600,
                                    lineHeight: 1.3 
                                  }}
                                >
                                  {node.privateDnsName || node.vmId || "N/A"}
                                </Typography>
                              </Tooltip>
                                
                              {/* Instance Type & Node Pool - Fixed Layout */}
                              <Box sx={{ borderTop: "1px solid #f1f5f9", pt: 0.5, mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                                
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ 
                                      whiteSpace: "nowrap",     // ← Prevents breaking
                                      minWidth: 85,             // ← Fixed width for label
                                      fontWeight: 500 
                                    }}
                                  >
                                    Instance Type:
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: 12.8, 
                                      fontWeight: 500,
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {node.instanceType || node.vmSize || "N/A"}
                                  </Typography>
                                </Box>
                                  
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ 
                                      whiteSpace: "nowrap", 
                                      minWidth: 85, 
                                      fontWeight: 500 
                                    }}
                                  >
                                    Node Pool:
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: 12.8, 
                                      fontWeight: 500,
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {node.nodeGroup || node.nodePoolName || "N/A"}
                                  </Typography>
                                </Box>
                                  
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.cpu && (
                        <TableCell sx={{ minWidth: 210 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>{cpuPct.toFixed(1)}%</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(node.usedCPU ?? 0)}/{(node.totalCPU || node.vcpus)}
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(cpuPct, 100)} sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" } }} />
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Peak: {node.peakCPUValue}%</Typography>
                            <Typography variant="caption" color="text.secondary">Idle: {node.idleDays}d</Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.memory && (
                        <TableCell sx={{ minWidth: 210 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>{memPct.toFixed(1)}%</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(node.usedMemory ?? 0)}/{(node.totalMemory || node.memory)} GB
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(memPct, 100)} sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#22c55e" } }} />
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Free: {node.freeMemory} GB</Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.storage && (
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                            {(node.size ?? node.totalDiskSize ?? 0)} GB
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>
                            {(node.volumeType || node.diskType || "Standard")}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.cost && (
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 700 }}>
                            ${ (node.nodeCost ?? 0).toFixed(2) }
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>
                            Monthly: ${(node.currentOnDemandMonthlyInstanceCost ?? 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.potentialSaving && (
                        <TableCell align="right" sx={{ minWidth: 140 }}>
                          {(() => {
                            const current = Number(node.currentOnDemandMonthlyInstanceCost) || 0;
                            const recommended = Number(node.recommendedOnDemandMonthlyPriceValue) || 0;
                            const saving = current - recommended;
                          
                            return (
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontSize: 13, 
                                  fontWeight: 700
                                }}
                              >
                                {saving > 0 ? `$${saving.toFixed(2)}` : "$0.00"}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                      )}
                      {visibleCols.efficiency && (
                        <TableCell sx={{ minWidth: 240 }}>   {/* ← Increased minWidth */}
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                              {node.nodeEfficiency === "NA" ? "NA" : `${effVal.toFixed(2)}`}
                            </Typography>
                            <Chip 
                              label={node.nodeEfficiencyLabel || effLabel} 
                              size="small" 
                              color={node.nodeEfficiency === "NA" ? "default" : effColor} 
                              sx={{ height: 18, fontSize: 10, fontWeight: 700 }} 
                            />
                          </Box>

                          <Box sx={{ borderTop: "1px solid #f1f5f9", pt: 0.5 }}>
                            {[
                              { label: "Running", val: `${node.nodeRunningHours || node.clusterNodesRunningHours || 0}h` },
                              { label: "Rec. Type", val: node.recommendedInstanceType || "NA" },
                            ].map(row => (
                              <Box 
                                key={row.label} 
                                sx={{ 
                                  display: "flex", 
                                  justifyContent: "space-between", 
                                  gap: 2,
                                  mb: 0.3   // small spacing between rows
                                }}
                              >
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ 
                                    whiteSpace: "nowrap",     // ← Prevents "Rec." from breaking
                                    flexShrink: 0 
                                  }}
                                >
                                  {row.label}:
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: "text.primary",
                                    textAlign: "right",
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {row.val}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f1f5f9", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{sorted.length}</strong> of <strong>{RAW_DATA.length}</strong> node{RAW_DATA.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Card>

        <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" variant="filled" sx={{ fontSize: 13, borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}